import { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Context } from '../../AuthContext';
import Spinner from '../../components/Spinner';
import SubmitButton from '../AuthPages/SubmitButton';

const ALLOWED_STATUSES = new Set(['trialing', 'active']);
const POLL_INTERVAL_MS = 1500;
const MAX_ATTEMPTS = 20;

const SubscriptionSuccessPage = () => {
  const { user, authLoading } = useContext(Context);
  const navigate = useNavigate();
  const [timedOut, setTimedOut] = useState(false);
  const cancelledRef = useRef(false);
  const intervalRef = useRef(null);
  const attemptsRef = useRef(0);

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const checkStatus = useCallback(async () => {
    if (cancelledRef.current || !user) return false;
    try {
      const token = await user.getIdToken();
      const response = await fetch(process.env.REACT_APP_FLASK_WEBSERVER + 'subscription_details', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return false;
      const res = await response.json();
      const status = res?.data?.subscription_status;
      if (ALLOWED_STATUSES.has(status)) {
        if (cancelledRef.current) return true;
        navigate('/tryrnai', { replace: true });
        return true;
      }
    } catch (error) {
      console.error('Subscription status check failed', error);
    }
    return false;
  }, [user, navigate]);

  const startPolling = useCallback(() => {
    setTimedOut(false);
    attemptsRef.current = 0;
    stopPolling();

    // Run an immediate check, then on an interval.
    checkStatus().then((done) => {
      if (done || cancelledRef.current) return;
      intervalRef.current = setInterval(async () => {
        attemptsRef.current += 1;
        const finished = await checkStatus();
        if (finished) {
          stopPolling();
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          stopPolling();
          if (!cancelledRef.current) setTimedOut(true);
        }
      }, POLL_INTERVAL_MS);
    });
  }, [checkStatus]);

  useEffect(() => {
    document.title = 'Setting up your account - The Simple Platform';
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    if (authLoading || !user) return undefined;

    startPolling();

    return () => {
      cancelledRef.current = true;
      stopPolling();
    };
  }, [authLoading, user, startPolling]);

  if (timedOut) {
    return (
      <div style={{ textAlign: 'center', maxWidth: '480px' }}>
        <h2>Almost there</h2>
        <p style={{ color: '#596274', fontSize: '16px' }}>
          We&rsquo;re still finalizing your account &mdash; please give it a moment, or contact
          support if this persists.
        </p>
        <SubmitButton
          text="Continue"
          width="50%"
          margin="20px auto 0 auto"
          onClick={() => startPolling()}
        />
      </div>
    );
  }

  return (
    <div style={{ textAlign: 'center' }}>
      <Spinner />
      <p style={{ color: '#596274', fontSize: '16px', marginTop: '16px' }}>
        Setting up your account&hellip;
      </p>
    </div>
  );
};

export default SubscriptionSuccessPage;
